import { useSyncExternalStore } from 'react'

/**
 * **Eine Liste, ein Abo, alle lesen mit.**
 *
 * Gebraucht, wo VIELE Bausteine dieselbe kleine Liste brauchen — die
 * Etiketten zum Beispiel, die jede Aufgabenzeile liest. Liest sie jede selbst,
 * hängen an einer Liste mit fünfunddreißig Zeilen auch fünfunddreißig Abos an
 * derselben Tabelle, und jedes liefert beim Feuern ein frisches Array.
 * `React.memo` greift dann nirgends (gemessen am 21.09.2026 mit `render-audit`:
 * `hook:tags×35`, dazu Commits über 200ms).
 *
 * Zwei Dinge macht der Speicher deshalb: Er hält **ein** Abo, solange jemand
 * zusieht, und er meldet sich nur, wenn der Fingerabdruck sich ändert —
 * Dexie feuert auch bei Nachbarschreibvorgängen und schickt dann
 * inhaltsgleiche, aber neue Objekte.
 *
 * @example
 * const tags = createSharedList(
 *   next => liveQuery(() => db.tags.toArray()).subscribe({ next, error: () => {} }).unsubscribe,
 *   items => items.map(baseEntityIdentity).join(','),
 * )
 * export const useSharedTags = tags.use
 */
export function createSharedList<T>(
  /** Startet das Abo und liefert zurück, wie man es wieder beendet. */
  observe: (next: (items: T[]) => void) => () => void,
  /** Kennung + Änderungsstand der ganzen Liste, als Zeichenkette. */
  sign: (items: T[]) => string,
) {
  let current: T[] = []
  /** `\0` kann kein Fingerabdruck sein — die erste Lieferung kommt also immer durch. */
  let fingerprint = '\0'
  const listeners = new Set<() => void>()
  let stop: (() => void) | null = null

  function subscribe(callback: () => void): () => void {
    listeners.add(callback)
    if (!stop) {
      stop = observe(items => {
        const next = sign(items)
        if (next === fingerprint) return
        fingerprint = next
        current = items
        listeners.forEach(fn => fn())
      })
    }
    return () => {
      listeners.delete(callback)
      if (listeners.size === 0) {
        stop?.()
        stop = null
      }
    }
  }

  const getSnapshot = () => current

  return {
    use: (): T[] => useSyncExternalStore(subscribe, getSnapshot, getSnapshot),
    /** Nur für Tests: beendet das Abo und vergisst den Stand. */
    reset: () => {
      stop?.()
      stop = null
      listeners.clear()
      current = []
      fingerprint = '\0'
    },
  }
}
